import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { ArrowRight, RotateCcw, Gift } from "lucide-react";
import { motion } from "framer-motion";
import { getSurveyCount } from "../lib/api";
import { useSurvey } from "../hooks/useSurvey";
import { Button } from "../components/shared/Button";
import { Footer } from "../components/shared/Footer";
import { MarketingHeader } from "../components/shared/MarketingHeader";
import { KlairIntro } from "../components/landing/KlairIntro";
import { AccountabilitySection } from "../components/landing/AccountabilitySection";
import { HeroPreview } from "../components/landing/HeroPreview";
import { CtaBand } from "../components/landing/CtaBand";

const REF_KEY = "klareet_ref";
const REF_RE = /^KLR[A-Z0-9]{5}$/;

function persistRef(code) {
    try {
        localStorage.setItem(REF_KEY, code);
    } catch {
        // ignore
    }
}

function readRef() {
    try {
        return localStorage.getItem(REF_KEY);
    } catch {
        return null;
    }
}

const CONTAINER = "max-w-6xl mx-auto px-6 sm:px-8 lg:px-12";

export default function Landing() {
    const navigate = useNavigate();
    const [search] = useSearchParams();
    const { hasDraft, reset, hydrated } = useSurvey();
    const [count, setCount] = useState(null);
    const [refCode, setRefCode] = useState(null);

    useEffect(() => {
        const urlRef = (search.get("ref") || "").trim().toUpperCase();
        if (urlRef && REF_RE.test(urlRef)) {
            persistRef(urlRef);
            setRefCode(urlRef);
        } else {
            const stored = readRef();
            if (stored && REF_RE.test(stored)) {
                setRefCode(stored);
            }
        }
    }, [search]);

    useEffect(() => {
        let alive = true;
        getSurveyCount().then((n) => {
            if (alive) setCount(n);
        });
        return () => {
            alive = false;
        };
    }, []);

    return (
        <div className="relative min-h-screen bg-klareet-NAVY">
            <MarketingHeader />

            {/* HERO */}
            <section
                className="relative overflow-hidden"
                data-testid="hero-section"
            >
                {/* soft glow */}
                <div
                    className="pointer-events-none absolute inset-0 -z-0"
                    style={{
                        background:
                            "radial-gradient(900px 500px at 80% 0%, rgba(20,184,166,0.12), transparent 60%), radial-gradient(700px 400px at 0% 0%, rgba(59,130,246,0.08), transparent 60%)",
                    }}
                    aria-hidden
                />
                <div
                    className={`${CONTAINER} relative z-10 pt-12 md:pt-20 pb-20 md:pb-28`}
                >
                    <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-14 items-center">
                        {/* Left column */}
                        <div className="lg:col-span-7">
                            <div
                                className="text-xs md:text-sm uppercase tracking-[0.2em] font-semibold text-klareet-T"
                                data-testid="hero-eyebrow"
                            >
                                Career Clarity · Market Survey
                            </div>
                            <h1
                                className="mt-5 text-5xl md:text-7xl font-bold tracking-tight text-klareet-TEXT leading-[1.02] max-w-xl"
                                data-testid="landing-headline"
                            >
                                Would you actually use a career tool that tells
                                you the{" "}
                                <span className="relative inline-block">
                                    <span className="relative z-10 text-klareet-T2">
                                        truth?
                                    </span>
                                    <span
                                        aria-hidden
                                        className="absolute left-0 right-0 bottom-1 md:bottom-2 h-3 md:h-5 bg-klareet-T/25 rounded-sm -z-0"
                                    />
                                </span>
                            </h1>
                            <p className="mt-7 text-base md:text-lg text-klareet-MUT leading-relaxed max-w-lg">
                                Klareet is being built by Flynaut. Before we
                                invest months building it, we want to know if
                                it solves a real problem for real people.
                            </p>
                            <p className="mt-4 text-sm md:text-base text-klareet-MUT/80 leading-relaxed">
                                10 questions. 4 minutes. You get a glimpse of
                                your personal snapshot at the end.
                            </p>

                            {refCode ? (
                                <motion.div
                                    initial={{ opacity: 0, y: 8 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    transition={{ duration: 0.35 }}
                                    className="mt-6 inline-flex items-center gap-3 px-4 py-2 rounded-full border border-klareet-T/40 bg-klareet-T/10 text-sm text-klareet-TEXT max-w-full"
                                    data-testid="ref-pill"
                                >
                                    <Gift
                                        size={16}
                                        className="text-klareet-T2 shrink-0"
                                    />
                                    <span className="truncate">
                                        Your friend invited you. They get 1
                                        year free if 20 join through their link
                                    </span>
                                </motion.div>
                            ) : null}

                            <div className="mt-9 flex flex-wrap gap-x-6 gap-y-3 items-center">
                                <Button
                                    size="lg"
                                    onClick={() => {
                                        if (!hasDraft) reset();
                                        navigate("/survey");
                                    }}
                                    data-testid="start-survey-btn"
                                >
                                    Start the survey
                                    <ArrowRight size={18} />
                                </Button>
                                <a
                                    href="#klair"
                                    onClick={(e) => {
                                        e.preventDefault();
                                        document
                                            .querySelector("#klair")
                                            ?.scrollIntoView({
                                                behavior: "smooth",
                                                block: "start",
                                            });
                                    }}
                                    className="text-sm md:text-[15px] font-medium text-klareet-T2 hover:text-klareet-TEXT transition-colors underline-offset-4 hover:underline"
                                    data-testid="see-how-link"
                                >
                                    See how it works →
                                </a>
                            </div>

                            <div className="mt-8 flex flex-wrap items-center gap-x-4 gap-y-2">
                                <div
                                    className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-klareet-bor bg-klareet-card/60 text-xs font-mono text-klareet-MUT"
                                    data-testid="response-counter"
                                >
                                    <span className="relative flex h-2 w-2">
                                        <span className="absolute inline-flex h-full w-full rounded-full bg-klareet-GREEN/60 animate-ping" />
                                        <span className="relative inline-flex h-2 w-2 rounded-full bg-klareet-GREEN" />
                                    </span>
                                    {count === null
                                        ? "loading…"
                                        : count === 0
                                          ? "Be the first to respond"
                                          : `${count} people have already responded`}
                                </div>
                                {hydrated && hasDraft ? (
                                    <button
                                        type="button"
                                        onClick={() => navigate("/survey")}
                                        className="inline-flex items-center gap-2 text-xs text-klareet-T2 hover:text-klareet-TEXT transition-colors"
                                        data-testid="resume-survey-btn"
                                    >
                                        <RotateCcw size={12} />
                                        Continue where you left off
                                    </button>
                                ) : null}
                                <button
                                    type="button"
                                    onClick={() => navigate("/returning")}
                                    className="inline-flex items-center gap-1.5 text-xs text-klareet-MUT hover:text-klareet-T2 transition-colors"
                                    data-testid="returning-link"
                                >
                                    Already taken it? View your snapshot →
                                </button>
                            </div>
                        </div>

                        {/* Right column — Hero preview */}
                        <div className="lg:col-span-5 mt-4 lg:mt-0">
                            <HeroPreview />
                        </div>
                    </div>
                </div>
            </section>

            <SectionDivider />

            {/* KLAIR */}
            <section
                id="klair"
                className="relative scroll-mt-24"
                data-testid="klair-section-wrap"
            >
                <div
                    className="pointer-events-none absolute inset-x-0 top-0 h-[420px] -z-0"
                    style={{
                        background:
                            "radial-gradient(800px 400px at 50% 0%, rgba(20,184,166,0.10), transparent 70%)",
                    }}
                    aria-hidden
                />
                <div className={`${CONTAINER} relative z-10 py-20 md:py-28`}>
                    <KlairIntro />
                </div>
            </section>

            <SectionDivider />

            {/* WHY / ACCOUNTABILITY */}
            <section
                id="why"
                className="relative bg-klareet-card scroll-mt-24"
                data-testid="why-section-wrap"
            >
                <div className={`${CONTAINER} py-20 md:py-28`}>
                    <AccountabilitySection />
                </div>
            </section>

            <CtaBand />

            <Footer />
        </div>
    );
}

function SectionDivider() {
    return (
        <div className="relative max-w-6xl mx-auto px-6 sm:px-8 lg:px-12">
            <div className="h-px w-3/5 mx-auto bg-klareet-bor opacity-70" />
        </div>
    );
}
